import React, { useState } from 'react';
import {
  Box, Drawer, AppBar, Toolbar, List, Typography, Divider,
  IconButton, ListItem, ListItemButton, ListItemIcon, ListItemText,
  Avatar, Menu, MenuItem, Chip, useTheme, TextField, InputAdornment, Collapse,
} from '@mui/material';
import {
  Menu as MenuIcon, Dashboard, People, Business, PhoneForwarded, PersonAdd, Shield,
  ReceiptLong, Route, Analytics, Security, Phone, Description,
  Dialpad, SupportAgent, Logout, Brightness4, Brightness7,
  AccountBalance, DeviceHub, Payment, Upload, SmartToy as SmartToyIcon,
  Inventory2, Subscriptions, DataUsage, Receipt as ReceiptRunIcon,
  PhoneInTalk, Terminal, TableChart, MonitorHeart, AccountTree, GraphicEq,
  PhoneAndroid, Assessment, Public, Summarize, VerifiedUser, MarkEmailRead, ContactMail,
  Search, ExpandLess, ExpandMore, Block, CompareArrows } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore, ROLE_LABELS, ROLE_COLORS, financialHidden, L2_PATHS } from '../../store/auth';

const DRAWER_WIDTH = 240;

// roles: which roles may see this item. No roles key = visible to all admin roles.
// financial: hidden for sub_super_admin / ops_admin. (Same rules as before — only the
// grouping/layout changed, not visibility.)

// Pinned single items
const DASHBOARD_ITEM: any = { path: '/dashboard', label: 'Dashboard', icon: <Dashboard /> };
// Dashboard sub-items. superOnly = exact super_admin role (hasRole lets sub_super_admin/ops_admin through).
const DASHBOARD_SUBITEMS: any[] = [
  { path: '/dashboard/bt-bill', label: 'BT Bill vs Assumed', icon: <CompareArrows />, superOnly: true, sub: true },
  { path: '/dashboard/health', label: 'Health Check', icon: <MonitorHeart />, roles: ['super_admin', 'provider_admin', 'technical_admin'], sub: true },
  { path: '/dashboard/health/security', label: 'Security Monitor', icon: <Security />, roles: ['super_admin', 'provider_admin', 'technical_admin'], sub: true },
];
const SUPPORT_ITEM: any   = { path: '/tickets',   label: 'Support',   icon: <SupportAgent /> };

// Admin navigation, grouped (collapsible). Every item keeps its exact roles/financial flags.
const NAV_GROUPS: { key: string; title: string; icon: React.ReactNode; items: any[] }[] = [
  { key: 'monitoring', title: 'Monitoring', icon: <MonitorHeart />, items: [
    { path: '/calls/live',         label: 'Live Calls & Monitor',    icon: <PhoneInTalk />, roles: ['super_admin', 'provider_admin', 'technical_admin', 'billing_admin', 'executive'] },
    { path: '/monitoring/failures', label: 'Failures & Service Health', icon: <MonitorHeart />, roles: ['super_admin', 'provider_admin', 'technical_admin', 'billing_admin', 'executive'] },
    { path: '/monitoring/compliance', label: 'Compliance Monitor', icon: <Shield />, roles: ['super_admin', 'provider_admin', 'technical_admin', 'billing_admin', 'executive'] },
    { path: '/cdr',                label: 'CDR (raw)',               icon: <TableChart /> },
    { path: '/calls/logs',         label: 'Log Viewer',              icon: <Terminal />, roles: ['super_admin', 'technical_admin'] },
  ]},
  { key: 'reports', title: 'Reports & Analytics', icon: <Assessment />, items: [
    { path: '/analytics',                 label: 'Analytics',          icon: <Analytics /> },
    { path: '/reports/customer-calls',    label: 'Customer Calls',     icon: <ReceiptLong /> },
    { path: '/reports/customer-summary',  label: 'Customer Summary',   icon: <Summarize /> },
    { path: '/reports/trunk-summary',     label: 'Trunk Summary',      icon: <DeviceHub /> },
    { path: '/reports/country',           label: 'Country Report',     icon: <Public /> },
    { path: '/reports/revenue-assurance', label: 'Revenue Assurance',  icon: <Assessment />, roles: ['reseller_admin', 'provider_admin', 'technical_admin', 'billing_admin', 'executive'], financial: true },
  ]},
  { key: 'customers', title: 'Customers', icon: <Business />, items: [
    { path: '/tenants',          label: 'Customers',                   icon: <Business />,    roles: ['reseller_admin', 'billing_admin'] },
    { path: '/onboarding',       label: 'Onboard Customer',            icon: <PersonAdd />,   roles: ['reseller_admin', 'billing_admin'] },
    { path: '/billing-contacts', label: 'Customer / Carrier Contacts', icon: <ContactMail />, roles: ['super_admin', 'provider_admin', 'billing_admin', 'reseller_admin', 'executive'], financial: true },
    { path: '/compliance',       label: 'Compliance / KYC',            icon: <VerifiedUser />, roles: ['reseller_admin', 'provider_admin', 'billing_admin'] },
  ]},
  { key: 'routing', title: 'Routing & Carriers', icon: <Route />, items: [
    { path: '/carriers',           label: 'Carriers',                     icon: <PhoneForwarded />, roles: ['provider_admin', 'technical_admin'] },
    { path: '/onboarding/carrier', label: 'Onboard Carrier',              icon: <AccountTree />,    roles: ['provider_admin', 'technical_admin'] },
    { path: '/rates',              label: 'Rate Plans',                   icon: <Description />,    roles: ['provider_admin', 'billing_admin'] },
    { path: '/rate-card',          label: 'Rate Card',                    icon: <TableChart />,     roles: ['provider_admin', 'billing_admin'] },
    { path: '/routing/destination-filters', label: 'Destination Filters', icon: <Shield />,         roles: ['provider_admin', 'technical_admin'] },
    { path: '/numbers',            label: 'DID Numbers',                  icon: <Dialpad />,        roles: ['provider_admin', 'technical_admin'] },
    { path: '/numbers/unassigned', label: 'Unassigned Numbers',           icon: <Inventory2 />,     roles: ['provider_admin', 'technical_admin', 'reseller_admin'] },
    { path: '/numbers/blocked',    label: 'Blocked Numbers',              icon: <Block />,          roles: ['provider_admin', 'technical_admin'] },
    { path: '/sip-devices',        label: 'Ingress Trunks & SIP Devices', icon: <PhoneAndroid />,   roles: ['provider_admin', 'technical_admin'] },
  ]},
  { key: 'billing', title: 'Billing', icon: <AccountBalance />, items: [
    { path: '/billing',          label: 'Balances',         icon: <AccountBalance />, roles: ['provider_admin', 'billing_admin', 'executive'], financial: true },
    { path: '/invoices',         label: 'Invoices',         icon: <ReceiptLong />,    roles: ['provider_admin', 'billing_admin', 'executive'], financial: true },
    { path: '/accounts',         label: 'Customer Accounts', icon: <AccountBalance />, roles: ['provider_admin', 'billing_admin'], financial: true },
    { path: '/billing-run',      label: 'Billing Run',      icon: <ReceiptRunIcon />, roles: ['super_admin', 'billing_admin'], financial: true },
    { path: '/billing-import',   label: 'Billing Import',   icon: <Upload />,         roles: ['super_admin', 'technical_admin', 'billing_admin'] },
    { path: '/payment-settings', label: 'Payment Gateways', icon: <Payment />,        roles: ['super_admin'], financial: true },
  ]},
  { key: 'products', title: 'Products & Plans', icon: <Inventory2 />, items: [
    { path: '/catalogue',     label: 'Product Catalogue', icon: <Inventory2 />,   roles: ['super_admin', 'provider_admin', 'billing_admin'] },
    { path: '/subscriptions', label: 'Subscriptions',     icon: <Subscriptions />, roles: ['super_admin', 'provider_admin', 'billing_admin'] },
    { path: '/bundle-usage',  label: 'Bundle Usage',      icon: <DataUsage />,     roles: ['super_admin', 'provider_admin', 'billing_admin'] },
  ]},
  { key: 'security', title: 'Security', icon: <Security />, items: [
    { path: '/fraud', label: 'Fraud Rules', icon: <Security />, roles: ['reseller_admin', 'technical_admin'] },
    { path: '/acl',   label: 'SIP ACL',     icon: <Security />, roles: ['super_admin', 'technical_admin'] },
  ]},
  { key: 'system', title: 'System', icon: <DeviceHub />, items: [
    { path: '/sbc',          label: 'SBC Nodes',               icon: <DeviceHub />,   roles: ['super_admin', 'technical_admin'] },
    { path: '/sbc-control',  label: 'SBC/Trunk Control Panel', icon: <DeviceHub />,   roles: ['super_admin', 'technical_admin', 'provider_admin'] },
    { path: '/users',        label: 'Users',                   icon: <People /> },
    { path: '/notifications', label: 'Notifications',          icon: <MarkEmailRead />, roles: ['super_admin'] },
    { path: '/ai-assistant', label: 'AI Assistant',            icon: <SmartToyIcon />, roles: ['super_admin'] },
    { path: '/audit',        label: 'Audit Log',               icon: <Shield />, superOnly: true },
  ]},
];

// Customer-portal nav (unchanged)
const CUSTOMER_ITEMS: any[] = [
  { path: '/customer/dashboard', label: 'My Dashboard',       icon: <Dashboard /> },
  { path: '/customer/cdrs',      label: 'Call History',       icon: <ReceiptLong /> },
  { path: '/customer/invoices',  label: 'Invoices',           icon: <Description /> },
  { path: '/customer/balance',   label: 'Balance & Payments', icon: <AccountBalance /> },
];

// Flat list of every item (for the AppBar title lookup)
const ALL_ITEMS: any[] = [
  DASHBOARD_ITEM, ...DASHBOARD_SUBITEMS, SUPPORT_ITEM,
  ...NAV_GROUPS.flatMap(g => g.items),
  ...CUSTOMER_ITEMS,
];

const GROUP_STATE_KEY = 'ce_nav_groups_open';

interface Props {
  children: React.ReactNode;
  darkMode: boolean;
  onToggleDark: () => void;
}

export const Layout: React.FC<Props> = ({ children, darkMode, onToggleDark }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, hasRole } = useAuthStore();
  const isCustomer = user?.role === 'customer_admin' || user?.role === 'customer_user';
  const isFinHidden = financialHidden(user?.role); // financial pages hidden for these roles
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [search, setSearch] = useState('');
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    try { return JSON.parse(localStorage.getItem(GROUP_STATE_KEY) || '{}'); } catch { return {}; }
  });

  const handleLogout = () => { logout(); navigate('/login'); };

  // A group is open unless explicitly collapsed (default open).
  const isGroupOpen = (key: string) => openGroups[key] !== false;
  const toggleGroup = (key: string) => {
    setOpenGroups(prev => {
      const next = { ...prev, [key]: prev[key] === false ? true : false };
      try { localStorage.setItem(GROUP_STATE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  // Visibility: same rules as before (financial mask + role gating).
  const canSee = (item: any) => {
    if (user?.role === 'l2_engineer') return L2_PATHS.has(item.path);
    if (item.superOnly && user?.role !== 'super_admin') return false;
    if (item.financial && isFinHidden) return false;
    if (item.roles && !hasRole(item.roles)) return false;
    return true;
  };

  const renderLink = (item: any) => {
    const active = location.pathname === item.path;
    return (
      <ListItem key={item.path} disablePadding>
        <ListItemButton
          onClick={() => { navigate(item.path); setMobileOpen(false); }}
          selected={active}
          sx={{
            borderRadius: '0 20px 20px 0', mr: 1, ...(item.sub ? { pl: 4 } : {}),
            '&.Mui-selected': {
              background: theme.palette.primary.main + '20',
              borderRight: '3px solid ' + theme.palette.primary.main,
              '& .MuiListItemIcon-root': { color: theme.palette.primary.main },
              '& .MuiListItemText-primary': { color: theme.palette.primary.main, fontWeight: 600 },
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 36, color: active ? theme.palette.primary.main : 'inherit' }}>{item.icon}</ListItemIcon>
          <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: '0.9rem' }} />
        </ListItemButton>
      </ListItem>
    );
  };

  const q = search.trim().toLowerCase();

  const renderNav = () => {
    // Customer portal: flat list of the customer items
    if (isCustomer) {
      const items = CUSTOMER_ITEMS.filter(i => !q || i.label.toLowerCase().includes(q));
      return items.map(renderLink);
    }

    // Admin — search mode: flatten every visible item, no group headers
    if (q) {
      const hits = ALL_ITEMS
        .filter(i => i.path && i !== undefined)
        .filter(canSee)
        .filter(i => i.label.toLowerCase().includes(q));
      // de-dup by path (ALL_ITEMS may include dashboard/support which are also standalone)
      const seen = new Set<string>();
      const unique = hits.filter(i => (seen.has(i.path) ? false : (seen.add(i.path), true)));
      if (unique.length === 0) {
        return <Typography variant="caption" sx={{ px: 2, py: 2, display: 'block', color: 'text.secondary' }}>No menu matches “{search}”.</Typography>;
      }
      return unique.map(renderLink);
    }

    // Admin — normal grouped mode
    return (
      <>
        {renderLink(DASHBOARD_ITEM)}
        {DASHBOARD_SUBITEMS.filter(canSee).map(renderLink)}
        {NAV_GROUPS.map(group => {
          const visible = group.items.filter(canSee);
          if (visible.length === 0) return null;
          const open = isGroupOpen(group.key);
          return (
            <Box key={group.key}>
              <ListItemButton onClick={() => toggleGroup(group.key)} sx={{ py: 0.5, mt: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary' }}>{group.icon}</ListItemIcon>
                <ListItemText
                  primary={group.title}
                  primaryTypographyProps={{ variant: 'caption', sx: { fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'text.secondary', fontSize: '0.68rem' } }}
                />
                {open ? <ExpandLess fontSize="small" sx={{ color: 'text.secondary' }} /> : <ExpandMore fontSize="small" sx={{ color: 'text.secondary' }} />}
              </ListItemButton>
              <Collapse in={open} timeout="auto" unmountOnExit>
                <List dense disablePadding>
                  {visible.map(renderLink)}
                </List>
              </Collapse>
            </Box>
          );
        })}
        {renderLink(SUPPORT_ITEM)}
      </>
    );
  };

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar sx={{ background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: '#fff', borderRadius: 1.5, px: 0.85, py: 0.55 }}>
            <img src="/synergytalk-logo.svg" alt="CallEssential" style={{ height: 20, display: 'block' }} />
          </Box>
          <Box sx={{ lineHeight: 1 }}>
            <Typography sx={{ color: 'white', fontWeight: 800, fontSize: '1.02rem', lineHeight: 1.05, letterSpacing: '-0.01em' }}>
              CallEssential
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.82)', fontWeight: 700, fontSize: '0.62rem', letterSpacing: '0.28em', textTransform: 'uppercase' }}>
              Edge&nbsp;SBC
            </Typography>
          </Box>
        </Box>
      </Toolbar>
      <Divider />
      <Box sx={{ p: 1 }}>
        <TextField
          size="small" fullWidth placeholder="Search menu…" value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
        />
      </Box>
      <List dense sx={{ flexGrow: 1, overflowY: 'auto', pb: 2 }}>
        {renderNav()}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar position='fixed' elevation={0} sx={{ width: { sm: 'calc(100% - ' + DRAWER_WIDTH + 'px)' }, ml: { sm: DRAWER_WIDTH + 'px' }, borderBottom: '1px solid ' + theme.palette.divider, bgcolor: 'background.paper', color: 'text.primary' }}>
        <Toolbar>
          <IconButton edge='start' onClick={() => setMobileOpen(!mobileOpen)} sx={{ mr: 2, display: { sm: 'none' } }}><MenuIcon /></IconButton>
          <Typography variant='h6' sx={{ flexGrow: 1, fontWeight: 600, color: 'text.primary' }}>
            {ALL_ITEMS.find(m => m.path === location.pathname)?.label || 'Dashboard'}
          </Typography>
          <IconButton onClick={onToggleDark}>{darkMode ? <Brightness7 /> : <Brightness4 />}</IconButton>
          <Chip
            avatar={<Avatar sx={{ bgcolor: theme.palette.primary.main, width: 28, height: 28, fontSize: '0.75rem' }}>{user?.first_name?.[0] || 'U'}</Avatar>}
            label={user?.first_name + ' ' + user?.last_name}
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{ ml: 1, cursor: 'pointer' }}
            variant='outlined'
          />
          <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
            <MenuItem disabled>
              <Box>
                <Typography variant='caption' display='block'>{user?.email}</Typography>
                <Chip
                  label={ROLE_LABELS[user?.role || ''] || user?.role}
                  size='small'
                  color={ROLE_COLORS[user?.role || ''] || 'default'}
                  sx={{ mt: 0.5 }}
                />
              </Box>
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => { navigate('/security'); setAnchorEl(null); }}><Shield fontSize='small' sx={{ mr: 1 }} /> Security & 2FA</MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}><Logout fontSize='small' sx={{ mr: 1 }} /> Logout</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box component='nav' sx={{ width: { sm: DRAWER_WIDTH }, flexShrink: { sm: 0 } }}>
        <Drawer variant='temporary' open={mobileOpen} onClose={() => setMobileOpen(false)} sx={{ display: { xs: 'block', sm: 'none' }, '& .MuiDrawer-paper': { width: DRAWER_WIDTH } }}>
          {drawer}
        </Drawer>
        <Drawer variant='permanent' sx={{ display: { xs: 'none', sm: 'block' }, '& .MuiDrawer-paper': { width: DRAWER_WIDTH, borderRight: '1px solid ' + theme.palette.divider } }} open>
          {drawer}
        </Drawer>
      </Box>
      <Box component='main' sx={{ flexGrow: 1, p: 3, width: { sm: 'calc(100% - ' + DRAWER_WIDTH + 'px)' }, mt: '64px', minHeight: 'calc(100vh - 64px)', bgcolor: 'background.default' }}>
        {children}
      </Box>
    </Box>
  );
};
